import { assertPopulated } from '@dreamstack/payload/src/lib/assertPopulated'
import { StaticPage } from '@dreamstack/payload/src/payload-types'
import { cn } from '@dreamstack/util/src/cn'
import Image from 'next/image'
import type { FunctionComponent, ReactNode } from 'react'
import { FadeIn } from './FadeIn'
import {
  TransparentGradientLeftToRight,
  TransparentGradientTopToBottom,
} from './Gradients'
import {
  BlackGreenFrame,
  BlackGreenFrameClipped,
  BlackGreenLineSmallClipped,
  GreenBlackLineSmall,
} from './HeroSvgs'
import { GridWithGaps, MainContainer } from './MainGrid'
import { H1Hero, HeadingH4 } from './Texts'

export type HeroSize =
  | 'large'
  | 'medium'
  | 'small'
  | 'international'
  | 'campaign'

const heroContainerBaseClass = 'w-full fixed flex-shrink-0'
const heroSizeClass: Record<NonNullable<HeroSize>, string> = {
  large: 'h-px-480 md:h-px-576 lg:h-px-1080',
  medium: 'h-px-320 md:h-px-576 lg:h-px-860',
  small: 'h-px-320 md:h-px-480 lg:h-px-480',
  international: 'h-px-480 text-center md:h-[798px]',
  campaign: 'bg-accentroGray-full h-px-320 md:h-[503px] ml:h-[798px]',
}

const heroImageDefaultClass = 'w-full h-full absolute overflow-hidden z-[-1]'
const heroImageCampaignClass =
  'w-full h-full md:flex-[8] lg:flex-[12] relative z-[-1]'

const campaignMediaStyles = `
  @media (min-width: 2200px) {
    .hero-campaign-image {
      max-width: calc(100% * 0.83);
    }
  }
`

const HeroImageContainer = ({
  size,
  children,
}: {
  size?: HeroSize
  children: ReactNode
}) => {
  if (size === 'campaign') {
    return (
      <div className={cn(heroImageCampaignClass, 'hero-campaign-image')}>
        <style>{campaignMediaStyles}</style>
        {children}
      </div>
    )
  }

  return <div className={heroImageDefaultClass}>{children}</div>
}

const heroTextContainerClass = 'h-full pt-px-96'

const HeroTextContainer = ({
  forceDarkBg,
  children,
}: {
  forceDarkBg: boolean
  children: ReactNode
}) => (
  <div className={cn(heroTextContainerClass, forceDarkBg && 'text-white')}>
    {children}
  </div>
)

const heroTitleInternationalClass =
  'text-px-28 leading-px-42 md:text-px-56 md:leading-px-70 font-semibold'
const heroInternationalSubtitleClass =
  'mt-px-16 md:mt-px-24 font-semibold text-px-20 leading-px-30 md:text-px-24 md:leading-px-36 lg:text-[28px] lg:leading-[42px]'
const heroInternationalWrapperClass = 'mt-px-24 md:mt-[224px] lg:mt-[304px]'

const heroTextPlacementBaseClass =
  'flex flex-col h-full break-words overflow-clip'

const HeroTextPlacement = ({
  size,
  textCentered,
  children,
}: {
  size?: HeroSize
  textCentered: boolean
  children: ReactNode
}) => (
  <div
    className={cn(
      heroTextPlacementBaseClass,
      size !== 'campaign' && 'justify-center',
      size === 'campaign' &&
        'justify-start pt-px-20 md:col-end-8 md:pt-[90px] ml:pt-[224px]',
      textCentered
        ? 'col-start-1 col-end-12 text-center md:col-start-3 md:col-end-11'
        : 'col-start-1 col-span-12'
    )}
  >
    {children}
  </div>
)

const BlackFrameSpaceBlocker = () => (
  <div className="h-full w-full relative flex-1 hidden lg:block" />
)

const heroImageOverlayClass = 'absolute inset-0 h-full w-full'
const heroImageOverlayZClass = 'absolute inset-0 h-full w-full z-10'

export type HeroContainerProps = StaticPage['hero'] & {
  size?: HeroSize
  forceDarkBg?: boolean
  noMarginBottom?: boolean
}

export const HeroContainer: FunctionComponent<
  React.PropsWithChildren<HeroContainerProps>
> = ({
  backgroundImage,
  title,
  subtitle,
  size,
  forceDarkBg = true,
  noMarginBottom = false,
}) => {
  const textCentered = size === 'large'
  assertPopulated(backgroundImage)
  const gradient =
    backgroundImage &&
    (size === 'large' ? (
      <TransparentGradientTopToBottom />
    ) : (
      <TransparentGradientLeftToRight />
    ))
  const backgroundIsVideo = !!backgroundImage?.mimeType?.startsWith('video')

  return (
    <div
      className={cn(
        heroContainerBaseClass,
        size && heroSizeClass[size],
        !noMarginBottom && 'mb-px-48 lg:mb-px-64',
        forceDarkBg && !backgroundImage && 'bg-accentroGray-full'
      )}
    >
      {!!backgroundImage && !!backgroundImage.url && (
        <div className="absolute inset-0 flex">
          {size === 'campaign' && <BlackFrameSpaceBlocker />}
          <HeroImageContainer size={size}>
            {backgroundIsVideo ? (
              <video
                loop
                autoPlay
                playsInline
                muted
                width="100%"
                height="100%"
                style={{ objectFit: 'cover', height: '100%' }}
              >
                <source
                  src={backgroundImage.url}
                  type={backgroundImage.mimeType ?? undefined}
                />
              </video>
            ) : (
              <Image
                fill
                src={backgroundImage.url}
                title={title || undefined}
                alt={backgroundImage.alt ?? ''}
                priority={true}
                style={{ objectFit: 'cover' }}
              />
            )}

            {gradient}
            {size === 'campaign' && (
              <>
                <div className={heroImageOverlayZClass}>
                  <BlackGreenFrame
                    preserveAspectRatio="xMinYMid meet"
                    className="hidden sm:block w-full h-auto col-span-full max-h-[320px] md:max-h-[503px] ml:max-h-[798px]"
                  />
                  <GreenBlackLineSmall
                    preserveAspectRatio="xMinYMid meet"
                    className="sm:hidden w-full h-auto col-span-full max-h-[320px] md:max-h-[503px] ml:max-h-[798px]"
                  />
                </div>
                <div className={heroImageOverlayClass}>
                  <BlackGreenFrameClipped
                    preserveAspectRatio="none"
                    className="hidden sm:block w-full h-auto absolute max-h-[320px] md:max-h-[503px] ml:max-h-[798px]"
                  />
                  <BlackGreenLineSmallClipped
                    preserveAspectRatio="none"
                    className="sm:hidden w-full absolute bottom-0 max-h-[320px] md:max-h-[503px] ml:max-h-[798px]"
                  />
                </div>
              </>
            )}
          </HeroImageContainer>
        </div>
      )}
      <HeroTextContainer forceDarkBg={forceDarkBg}>
        <MainContainer className="h-full">
          <GridWithGaps className="h-full">
            <HeroTextPlacement size={size} textCentered={textCentered}>
              <FadeIn>
                {size === 'international' && (
                  <div className={heroInternationalWrapperClass}>
                    <div
                      className={heroTitleInternationalClass}
                      dangerouslySetInnerHTML={{ __html: title ?? '' }}
                    />
                    {subtitle && (
                      <div className={heroInternationalSubtitleClass}>
                        {subtitle}
                      </div>
                    )}
                  </div>
                )}
                {size !== 'international' && (
                  <div>
                    <H1Hero dangerouslySetInnerHTML={{ __html: title ?? '' }} />
                    {subtitle && <HeadingH4>{subtitle}</HeadingH4>}
                  </div>
                )}
              </FadeIn>
            </HeroTextPlacement>
          </GridWithGaps>
        </MainContainer>
      </HeroTextContainer>
    </div>
  )
}
